import { useState, useEffect } from 'react'
import { X, Loader2, Trash2, Save, User, Clock, Calendar, Scissors, AlertTriangle } from 'lucide-react'

const API_URL = (import.meta.env.VITE_API_URL as string) || 'https://salonflow-api.empty-mud-1234.workers.dev'

interface Stylist {
  id: string
  name: string
  role: string
  color_hex: string
}

interface Appointment {
  id: string
  service: string
  date: string
  time: string
  status: string
  duration: number
  notes: string | null
  customer_name: string
  stylist_id: string | null
  stylist_name?: string
  stylist_color?: string
  price?: number
}

interface AppointmentModalProps {
  appointment: Appointment
  onClose: () => void
  onSave: (updated: Appointment) => void
  onCancel: (appointmentId: string) => void
}

export function AppointmentModal({ appointment, onClose, onSave, onCancel }: AppointmentModalProps) {
  const [formData, setFormData] = useState({
    service: appointment.service,
    date: appointment.date,
    time: appointment.time,
    duration: appointment.duration,
    notes: appointment.notes || '',
    stylist_id: appointment.stylist_id || '',
    status: appointment.status
  })
  
  const [stylists, setStylists] = useState<Stylist[]>([])
  const [services, setServices] = useState<{ name: string; duration: number; price: number }[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchStylists()
    fetchServices()
  }, [])

  async function fetchStylists() {
    try {
      const response = await fetch(`${API_URL}/api/stylists?active=1`)
      const data = await response.json()
      setStylists(data.stylists || [])
    } catch (err) {
      console.error('Failed to fetch stylists:', err)
    }
  }

  async function fetchServices() {
    try {
      const response = await fetch(`${API_URL}/api/services`)
      const data = await response.json()
      setServices(data.services || [])
    } catch (err) {
      console.error('Failed to fetch services:', err)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSaving(true)
    setError(null)

    try {
      const response = await fetch(`${API_URL}/api/appointments/${appointment.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })

      const result = await response.json()
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to update appointment')
      }

      // Return updated appointment
      onSave({
        ...appointment,
        ...formData,
        stylist_name: stylists.find(s => s.id === formData.stylist_id)?.name,
        stylist_color: stylists.find(s => s.id === formData.stylist_id)?.color_hex
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancelConfirm = async () => {
    setIsCancelling(true)
    setError(null)

    try {
      const response = await fetch(`${API_URL}/api/appointments/${appointment.id}/cancel`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: cancelReason })
      })

      if (!response.ok) {
        const result = await response.json()
        throw new Error(result.error || 'Failed to cancel appointment')
      }

      onCancel(appointment.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel')
      setShowCancelConfirm(false)
    } finally {
      setIsCancelling(false)
    }
  }

  const handleServiceChange = (serviceName: string) => {
    const service = services.find(s => s.name === serviceName)
    setFormData(prev => ({
      ...prev,
      service: serviceName,
      duration: service?.duration || prev.duration
    }))
  }

  const selectedStylist = stylists.find(s => s.id === formData.stylist_id)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Edit Appointment</h2>
            <p className="text-sm text-gray-500 mt-1">{appointment.customer_name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Customer (read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="h-4 w-4 inline mr-1" />
              Customer
            </label>
            <div className="px-4 py-3 bg-gray-50 rounded-lg text-gray-900">
              {appointment.customer_name}
            </div>
          </div>

          {/* Service */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Scissors className="h-4 w-4 inline mr-1" />
              Service
            </label>
            <select
              value={formData.service}
              onChange={(e) => handleServiceChange(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {services.map(s => (
                <option key={s.name} value={s.name}>
                  {s.name} ({s.duration} min)
                </option>
              ))}
            </select>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="h-4 w-4 inline mr-1" />
                Date
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="h-4 w-4 inline mr-1" />
                Time
              </label>
              <input
                type="time"
                value={formData.time}
                onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Duration (minutes)
            </label>
            <input
              type="number"
              value={formData.duration}
              onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value) || 60 }))}
              min={15}
              step={15}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Stylist */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Stylist
            </label>
            <select
              value={formData.stylist_id}
              onChange={(e) => setFormData(prev => ({ ...prev, stylist_id: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Unassigned</option>
              {stylists.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.role})
                </option>
              ))}
            </select>
            {selectedStylist && (
              <div className="flex items-center gap-2 mt-2">
                <span 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: selectedStylist.color_hex }}
                />
                <span className="text-sm text-gray-600">{selectedStylist.name}</span>
              </div>
            )}
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
              placeholder="Any special requests or notes..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setShowCancelConfirm(true)}
              disabled={appointment.status === 'cancelled'}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 className="h-4 w-4" />
              Cancel Appointment
            </button>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save Changes
              </button>
            </div>
          </div>
        </form>

        {/* Cancellation Confirmation Modal */}
        {showCancelConfirm && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-xl">
            <div className="bg-white rounded-xl shadow-xl m-4 max-w-sm w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-full">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Cancel Appointment?</h3>
              </div>
              
              <p className="text-gray-600 mb-4">
                This will cancel <span className="font-semibold">{appointment.customer_name}'s</span> {appointment.service} on {appointment.date} at {appointment.time}.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cancellation Reason
                </label>
                <select
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="">Select a reason...</option>
                  <option value="Customer requested">Customer requested</option>
                  <option value="Customer no-show">Customer no-show</option>
                  <option value="Stylist unavailable">Stylist unavailable</option>
                  <option value="Salon closed">Salon closed</option>
                  <option value="Double booking">Double booking error</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowCancelConfirm(false)
                    setCancelReason('')
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Go Back
                </button>
                <button
                  onClick={handleCancelConfirm}
                  disabled={isCancelling || !cancelReason}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCancelling ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                  Yes, Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
